import 'react-widgets/styles.css';
import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import classnames from 'classnames';
import DateTimePicker from 'react-widgets/DatePicker';
import Localization from 'react-widgets/Localization';
import Moment from 'moment';
import MomentLocalizer from 'react-widgets-moment';
import styles from './date-picker.scss';
import { convertToDateOnlyString, parseStandardFormat } from 'helpers';
import useBreakpoint from 'hooks/useBreakpoint';
import { throttle } from 'lodash'; // Lodash used for throttling

Moment.locale('en-nz', {
  parentLocale: 'en-nz',
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'Just now',
    ss: '%d s',
    m: '1 m',
    mm: '%d m',
    h: '1 h',
    hh: '%d h',
    d: '1 d',
    dd: '%d d',
    w: '1 w',
    ww: '%d w',
    M: '1 m',
    MM: '%d m',
    y: '1 y',
    yy: '%d y',
  },
});

const getValueClamped = (value: Date, min: Date, max: Date) => {
  if (!value) return value;

  const valueTime = value.getTime();

  if (min?.getTime() > valueTime || valueTime > max?.getTime()) {
    return '';
  }

  return value;
};

const DatePicker = (props) => {
  const {
    className,
    onChange,
    onOpen,
    name,
    containerClassName,
    disabled,
    max,
    min,
    wrapClassName,
  } = props;

  const ref = useRef(null);
  const [dropUp, setDropUp] = useState(false);
  const [open, setOpen] = useState(false);
  const breakPoint = useBreakpoint();
  const isMobile = useMemo(
    () => breakPoint === 'sm' || breakPoint === 'xs',
    [breakPoint],
  );

  const updateDropUp = useCallback(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const shouldDropUp = rect.top + 260 > window.innerHeight;
      setDropUp(shouldDropUp);
    }
  }, []);

  // Throttle the updateDropUp function
  const throttledUpdateDropUp = useMemo(
    () => throttle(updateDropUp, 100),
    [updateDropUp],
  );

  useEffect(() => {
    // Open datepicker upwards when datepicker goes over viewport
    throttledUpdateDropUp();
    window.addEventListener('resize', throttledUpdateDropUp);
    window.addEventListener('scroll', throttledUpdateDropUp);
    return () => {
      window.removeEventListener('resize', throttledUpdateDropUp);
      window.removeEventListener('scroll', throttledUpdateDropUp);
    };
  }, [throttledUpdateDropUp]);

  const handleToggle = useCallback(() => {
    if (!disabled) {
      setOpen(!open);
      if (onOpen) {
        onOpen(!open);
      }
    }
  }, [disabled, open, onOpen]);

  const handleChange = useCallback(
    (v: Date) => {
      const val = getValueClamped(v, min, max);
      let value;
      switch (name) {
        case 'caStartFilter':
          value = val ? Moment(val).startOf('D').format() : undefined;
          break;
        case 'caEndFilter':
          value = val ? Moment(val).endOf('D').format() : undefined;
          break;
        default:
          value = val ? val.toISOString() : undefined;
          break;
      }
      setOpen(false);
      const target = { name, value };
      onChange({ target });
    },
    [onChange, name, min, max],
  );

  const handleInputChange = useCallback(
    (e) => {
      const v = getValueClamped(new Date(e.target.value), min, max);
      const val = v ? Moment(v).toISOString() : undefined;
      const target = { name, value: val };
      onChange({ target });
    },
    [onChange, name, min, max],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.keyCode === 13) {
        e.preventDefault();
        const momentVal = parseStandardFormat(e.target.value);
        if (momentVal.isValid()) {
          const val = momentVal.toISOString();
          const target = { name, value: val };
          onChange({ target });
        }
      }
    },
    [onChange],
  );

  const date = props.value ? new Date(props.value) : null;

  const defaultCurrentDate = useMemo(
    () =>
      !props.value && max && Moment().isAfter(Moment(max)) ? max : undefined,
    [props.value, max],
  );

  const datePickerProps = {
    ...props,
    valueDisplayFormat: 'DD/MM/YYYY',
    valueEditFormat: 'DD/MM/YYYY',
    value: date,
    className: classnames(styles.date, className),
    containerClassName: classnames(styles.dateContainer, containerClassName),
    open: open,
    onToggle: handleToggle,
    onChange: handleChange,
    defaultCurrentDate: defaultCurrentDate,
    onKeyDown: handleKeyDown,
    dropUp: dropUp,
  };

  return (
    <div className={classnames(styles.wrap, wrapClassName)} ref={ref}>
      <Localization date={new MomentLocalizer(Moment)}>
        {isMobile ? (
          <input
            className={styles.hiddenInput}
            name={name}
            type="date"
            value={props.value ? convertToDateOnlyString(props.value) : ''}
            onChange={handleInputChange}
            max={max ? convertToDateOnlyString(max) : ''}
            min={min ? convertToDateOnlyString(min) : ''}
            disabled={disabled}
          />
        ) : null}
        <DateTimePicker {...datePickerProps} />
      </Localization>
    </div>
  );
};

export default DatePicker;
